import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import * as Sentry from '@sentry/vue';
import { REALTIME_FAMILIES } from 'dashboard/helper/realtimeDelivery';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';

const MAX_DISCONNECT_SECONDS = 10800;

// The disconnect delay threshold is added to account for delays in identifying
// disconnections (for example, the websocket disconnection takes up to 3 seconds)
// while fetching the latest updated conversations or messages.
const DISCONNECT_DELAY_THRESHOLD = 15;

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;

    this.setupEventListeners();
  }

  disconnect = () => this.removeEventListeners();

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
    emitter.on(BUS_EVENTS.REALTIME_CHANNEL_STALE, this.onRealtimeChannelStale);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
    emitter.off(BUS_EVENTS.REALTIME_CHANNEL_STALE, this.onRealtimeChannelStale);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  fetchConversations = async (
    updatedWithin = this.getSecondsSinceDisconnect() +
      DISCONNECT_DELAY_THRESHOLD
  ) => {
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin,
    });
    await this.store.dispatch('fetchAllConversations');
    // Reset the updatedWithin in the store chat list filter after fetching conversations when the user is reconnected
    await this.store.dispatch('updateChatListFilters', {
      updatedWithin: null,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    await this.store.dispatch('fetchFilteredConversations', {
      queryData,
      page: 1,
    });
  };

  fetchConversationsOnReconnect = async updatedWithin => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations(updatedWithin);
    }
  };

  // Conversa aberta pela rota `conversation_id`. A rota da Inbox view
  // (`:type/:id`) não tem esse param — quem sabe a conversa (o detector de
  // canal morto) passa o id explícito.
  routeConversationId = () =>
    this.router.currentRoute.value.params.conversation_id;

  fetchConversationMessagesOnReconnect = async (
    conversationId = this.routeConversationId()
  ) => {
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    } else if (isNotificationRoute(currentRoute)) {
      await this.fetchNotificationsOnReconnect();
    }
  };

  setConversationLastMessageId = async (
    conversationId = this.routeConversationId()
  ) => {
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  // Canal morto (useSafetyRefetch): o socket nunca caiu, então não há
  // disconnectTime — a janela de rebusca é desde a última conferência que
  // bateu (`since`), com a mesma folga da reconexão. Mesmo caminho da
  // reconexão, só a parte que divergiu.
  // Mensagens: o id vem no payload (`conversationId`, do detector) — a rota
  // nem sempre tem `conversation_id` (Inbox view). Sem id não há o que
  // curar, e isso é bug do emissor: denuncia em vez de sair calado.
  onRealtimeChannelStale = async ({ family, since, conversationId }) => {
    if (family === REALTIME_FAMILIES.MESSAGES) {
      if (!conversationId) {
        Sentry.captureMessage(
          'realtime: canal morto de mensagens sem conversationId — sem cura',
          { level: 'warning', tags: { realtime_family: family } }
        );
        return;
      }
      await this.setConversationLastMessageId(conversationId);
      await this.fetchConversationMessagesOnReconnect(conversationId);
      return;
    }
    const updatedWithin =
      Math.max(differenceInSeconds(new Date(), since), 0) +
      DISCONNECT_DELAY_THRESHOLD;
    await this.fetchConversationsOnReconnect(updatedWithin);
  };

  onReconnect = async () => {
    await this.handleRouteSpecificFetch();
    await this.revalidateCaches();
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
  };
}

export default ReconnectService;
